import type { jsPDF } from "jspdf";
import { CellSpec, INK, PAGE, PdfDoc, TableSpec, TextStyle } from "./pdfDoc";

/**
 * Characters the standard PDF fonts cannot draw, and what stands in for them.
 * They carry no Unicode beyond Windows-1252, and jsPDF prints anything else as
 * spaced-out junk; the dashboard's own formatters use a real minus sign.
 */
const STAND_INS: Record<string, string> = { "\u2212": "-", "\u2009": " ", "\u202f": " ", "\u00a0": " ", "\u2265": ">=", "\u2264": "<=", "\u2192": "->" };

export function pdfSafe(value: string): string {
  return value.replace(/[\u2212\u2009\u202f\u00a0\u2265\u2264\u2192]/g, (char) => STAND_INS[char]);
}

/**
 * Draws a PdfDoc. jsPDF and its table plugin are loaded on first use, so the
 * dashboard's own bundle does not carry them for admins who never export.
 */
export async function renderPdf(model: PdfDoc): Promise<jsPDF> {
  const [{ jsPDF: JsPdf }, { autoTable }] = await Promise.all([import("jspdf"), import("jspdf-autotable")]);
  const doc = new JsPdf({ unit: "pt", format: "letter", orientation: model.orientation });
  const page = PAGE[model.orientation];
  const bottom = page.height - model.margin.bottom;
  let y = model.margin.top;

  const use = (style: TextStyle) => {
    doc.setFont(style.font, style.style);
    doc.setFontSize(style.size);
    doc.setTextColor(...style.color);
  };
  /** Moves to a new page when `height` more points would not fit. */
  const room = (height: number) => {
    if (y + height > bottom && y > model.margin.top) {
      doc.addPage();
      y = model.margin.top;
    }
  };

  for (const block of model.blocks) {
    if (block.kind === "pageBreak") {
      doc.addPage();
      y = model.margin.top;
      continue;
    }
    if (block.kind === "table") {
      y += block.gapBefore;
      room(60);
      drawTable(doc, autoTable, block.table, y, model);
      y = (doc as any).lastAutoTable.finalY;
      continue;
    }
    use(block.style);
    const lines: string[] = doc.splitTextToSize(pdfSafe(block.text), block.maxWidth);
    y += block.gapBefore;
    room(Math.min(lines.length, 2) * block.lineHeight);
    if (block.kind === "bullet") {
      // A drawn dot: "●" is not in the standard fonts' character set.
      const radius = block.bulletSize * 0.27;
      doc.setFillColor(...block.style.color);
      doc.circle(block.bulletX + radius, y + block.lineHeight * 0.5, radius, "F");
    }
    for (const line of lines) {
      room(block.lineHeight);
      doc.text(line, block.x, y, { baseline: "top" });
      y += block.lineHeight;
    }
  }

  if (model.footer !== null) {
    const pages = doc.getNumberOfPages();
    for (let n = 1; n <= pages; n++) {
      doc.setPage(n);
      use({ font: "helvetica", style: "normal", size: 8, color: INK.muted });
      const line = `${model.footer}${model.footer ? "   ·   " : ""}Page ${n} of ${pages}`;
      doc.text(line, model.margin.left, page.height - model.margin.bottom / 2, { baseline: "middle" });
    }
  }
  return doc;
}

const cellStyles = (cell: CellSpec, base: TextStyle, table: TableSpec) => {
  const style = { ...base, ...cell.style };
  const padding = table.padding;
  const width = table.lineWidth;
  return {
    ...(cell.rules ? { lineWidth: { top: width, right: width, bottom: width, left: width, ...cell.rules } } : {}),
    ...(cell.padding ? { cellPadding: { ...padding, ...cell.padding } } : {}),
    font: style.font,
    fontStyle: style.style,
    fontSize: style.size,
    textColor: style.color,
    ...(cell.fill ? { fillColor: cell.fill } : {}),
    ...(cell.align ? { halign: cell.align } : {}),
  };
};

function drawTable(doc: jsPDF, autoTable: (doc: any, options: any) => void, table: TableSpec, startY: number, model: PdfDoc) {
  const headText = { ...table.text, ...table.headText };
  const row = (cells: CellSpec[], base: TextStyle) =>
    cells.map((cell) => ({ content: pdfSafe(cell.text), colSpan: cell.colSpan, styles: cellStyles(cell, base, table) }));
  autoTable(doc, {
    theme: "plain",
    startY,
    margin: { left: table.x, right: 0, top: model.margin.top, bottom: model.margin.bottom },
    tableWidth: table.widths.reduce((sum, width) => sum + width, 0),
    showHead: table.showHead,
    // A row stays on one page, as in the team's report.
    rowPageBreak: "avoid",
    head: table.head.map((cells) => row(cells, headText)),
    body: table.body.map((cells) => row(cells, table.text)),
    styles: {
      font: table.text.font,
      fontStyle: table.text.style,
      fontSize: table.text.size,
      textColor: table.text.color,
      lineColor: table.lineColor,
      lineWidth: table.lineWidth,
      cellPadding: table.padding,
      valign: table.valign,
      overflow: "linebreak",
      ...(table.rowHeight ? { minCellHeight: table.rowHeight } : {}),
    },
    headStyles: {
      ...(table.headFill ? { fillColor: table.headFill } : {}),
      ...(table.headHeight ? { minCellHeight: table.headHeight } : {}),
      valign: "top",
    },
    columnStyles: Object.fromEntries(table.widths.map((width, index) => [index, { cellWidth: width }])),
  });
}

/** The finished file, for the preview frame and for saving. */
export async function pdfBlob(model: PdfDoc): Promise<Blob> {
  return (await renderPdf(model)).output("blob");
}
